import React, { useEffect, useState } from 'react';
import { getSessionHeaders } from '../../lib/serverSession';
import { AnnouncementBuilder, BuilderElement } from './AnnouncementBuilder';

type DeliveryCounts = {
  pending: number;
  sending: number;
  sent: number;
  failed: number;
};

type Audience = 'shareholder' | 'affiliate' | 'both';

type Announcement = {
  id: string;
  title: string;
  body: string;
  blocks?: BuilderElement[];
  status: 'draft' | 'published';
  audience?: Audience;
  published_at?: string | null;
  delivery?: DeliveryCounts;
};

function blocksFromAnnouncement(item: Announcement): BuilderElement[] {
  if (item.blocks?.length) return item.blocks;
  if (item.body) return [{ type: 'text', text: item.body }];
  return [];
}

const EMPTY_FORM = { id: '', title: '', blocks: [] as BuilderElement[], status: 'draft' as const, audience: 'both' as Audience };

async function postAnnouncement(operation: string, data?: Record<string, unknown>) {
  const headers = getSessionHeaders();
  if (!headers['X-Session-Id']) {
    throw new Error('Sign in again to manage announcements.');
  }
  const response = await fetch('/api/admin/announcements', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...headers },
    body: JSON.stringify({ operation, data }),
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok || body?.success === false) {
    throw new Error(body?.error || 'Announcement request failed.');
  }
  return body;
}

const AnnouncementManager: React.FC = () => {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const result = await postAnnouncement('list');
      setAnnouncements(result.announcements || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load announcements.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async (status: 'draft' | 'published') => {
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const result = await postAnnouncement('save', {
        id: form.id || undefined,
        title: form.title,
        blocks: form.blocks,
        audience: form.audience,
        status,
      });
      if (result.emailSkipped) {
        setNotice('Published. People will see it on the Updates page and when they sign in. No email was sent.');
        setForm(EMPTY_FORM);
        await load();
        return;
      }
      if (result.enqueued) {
        const sent = result.emailRun?.sent || 0;
        setNotice(
          result.emailRun?.reason === 'email_not_configured'
            ? 'Published. Email delivery is waiting because the email service is not configured. It will not be queued again when you edit this notice.'
            : `Published. Emails go out in groups of up to 80 (${sent} sent in this request). The rest continue in the background. Editing this notice will not email shareholders again.`
        );
      } else if (status === 'published') {
        setNotice('Saved. This did not send another email and did not clear acknowledgements.');
      } else {
        setNotice('Draft saved.');
      }
      if (result.translationWarning) {
        setError(result.translationWarning);
      }
      if (result.enqueueError) {
        setError(`The notice was published, but the recipient list could not be finished: ${result.enqueueError}`);
      }
      setForm(EMPTY_FORM);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the announcement.');
    } finally {
      setSaving(false);
    }
  };

  const removeDraft = async (id: string) => {
    setError('');
    setNotice('');
    try {
      await postAnnouncement('delete_draft', { id });
      if (form.id === id) setForm(EMPTY_FORM);
      setNotice('Draft deleted.');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not delete the draft.');
    }
  };

  const retryFailed = async (id: string) => {
    setError('');
    setNotice('');
    try {
      const result = await postAnnouncement('retry_failed', { id });
      setNotice(`Failed deliveries were queued again. Sent in this request: ${result.emailRun?.sent || 0}. Already sent mail was left alone.`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not retry failed emails.');
    }
  };

  const sendPending = async () => {
    setError('');
    setNotice('');
    try {
      const result = await postAnnouncement('send_pending');
      setNotice(`Pending mail processed. Sent in this request: ${result.emailRun?.sent || 0}.`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send pending emails.');
    }
  };

  const editingPublished = announcements.some((item) => item.id === form.id && item.status === 'published');

  return (
    <div>
      <h2 style={{ margin: '0 0 8px', color: '#fbbf24' }}>Announcements</h2>
      <p style={{ margin: '0 0 20px', color: '#d1d5db', lineHeight: 1.5 }}>
        Upload pictures and videos to the gallery, then drag them onto the page. A picture is saved small for the page and large for the slideshow. The email stays text and sends people to Updates.
        Choose who should see it. Shareholder and Both go to every active account. Affiliate goes to accounts that already have referrals.
        The email and the on-screen update use the language that person saved. English is used until they choose one.
        A later edit does not send the email again and does not clear confirmations.
      </p>

      {error ? <p style={{ color: '#fecaca' }}>{error}</p> : null}
      {notice ? <p style={{ color: '#bbf7d0' }}>{notice}</p> : null}

      <style>{`@media (max-width: 800px) { .announcement-admin-layout { grid-template-columns: 1fr !important; } }`}</style>
      <div className="announcement-admin-layout" style={{ display: 'grid', gridTemplateColumns: 'minmax(240px, 320px) 1fr', gap: '20px' }}>
        <div>
          <button
            type="button"
            onClick={() => { setForm(EMPTY_FORM); setNotice(''); setError(''); }}
            style={buttonStyle('#d4af37', '#111827')}
          >
            New announcement
          </button>
          {loading ? <p style={{ color: '#d1d5db' }}>Loading...</p> : null}
          {!loading && announcements.length === 0 ? <p style={{ color: '#d1d5db' }}>No announcements yet.</p> : null}
          {announcements.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setForm({
                id: item.id,
                title: item.title,
                blocks: blocksFromAnnouncement(item),
                status: item.status,
                audience: item.audience || 'both',
              })}
              style={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                marginTop: '10px',
                padding: '12px',
                borderRadius: '10px',
                border: form.id === item.id ? '1px solid #d4af37' : '1px solid #1f2937',
                background: '#0b0b0b',
                color: '#f9fafb',
                cursor: 'pointer',
              }}
            >
              <strong>{item.title}</strong>
              <div style={{ marginTop: '4px', color: '#d1d5db', fontSize: '13px' }}>
                {item.status === 'published' ? 'Published' : 'Draft'}
                {item.audience === 'affiliate' ? ' · Affiliate' : item.audience === 'shareholder' ? ' · Shareholder' : ' · Shareholder and affiliate'}
                {item.delivery ? ` · sent ${item.delivery.sent} · pending ${item.delivery.pending} · failed ${item.delivery.failed}` : ''}
              </div>
            </button>
          ))}
        </div>

        <form
          onSubmit={(event) => {
            event.preventDefault();
            save(editingPublished ? 'published' : 'draft');
          }}
        >
          <label style={labelStyle}>
            Title
            <input
              value={form.title}
              maxLength={200}
              onChange={(event) => setForm({ ...form, title: event.target.value })}
              style={inputStyle}
            />
          </label>
          <label style={labelStyle}>
            Who should see this
            <select
              value={form.audience}
              onChange={(event) => setForm({ ...form, audience: event.target.value as Audience })}
              style={inputStyle}
            >
              <option value="both">Shareholders and affiliates</option>
              <option value="shareholder">Shareholders only</option>
              <option value="affiliate">Affiliates only</option>
            </select>
          </label>
          <AnnouncementBuilder
            blocks={form.blocks}
            onChange={(blocks) => setForm((current) => ({ ...current, blocks }))}
            onError={setError}
          />

          {editingPublished ? (
            <p style={{ color: '#fde68a', lineHeight: 1.5 }}>
              This notice is already published. Saving changes the message shareholders still need to acknowledge.
              It does not email them again and it does not clear people who have already acknowledged it.
            </p>
          ) : null}

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
            {editingPublished ? (
              <button type="submit" disabled={saving} style={buttonStyle('#d4af37', '#111827')}>
                {saving ? 'Saving...' : 'Save changes'}
              </button>
            ) : (
              <>
                <button type="submit" disabled={saving} style={buttonStyle('#374151', '#f9fafb')}>
                  {saving ? 'Saving...' : 'Save draft'}
                </button>
                <button type="button" disabled={saving} onClick={() => save('published')} style={buttonStyle('#d4af37', '#111827')}>
                  {saving ? 'Publishing...' : 'Publish'}
                </button>
              </>
            )}
            {form.id && !editingPublished ? (
              <button type="button" onClick={() => removeDraft(form.id)} style={buttonStyle('#7f1d1d', '#fecaca')}>
                Delete draft
              </button>
            ) : null}
          </div>

          {editingPublished ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '16px' }}>
              <button type="button" onClick={() => retryFailed(form.id)} style={buttonStyle('#1f2937', '#f9fafb')}>
                Retry failed emails
              </button>
              <button type="button" onClick={sendPending} style={buttonStyle('#1f2937', '#f9fafb')}>
                Send pending emails
              </button>
            </div>
          ) : null}
        </form>
      </div>
    </div>
  );
};

function buttonStyle(background: string, color: string): React.CSSProperties {
  return {
    background,
    color,
    border: 'none',
    borderRadius: '8px',
    padding: '10px 14px',
    fontWeight: 700,
    cursor: 'pointer',
  };
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  marginBottom: '14px',
  color: '#f9fafb',
  fontWeight: 600,
};

const inputStyle: React.CSSProperties = {
  display: 'block',
  width: '100%',
  marginTop: '6px',
  boxSizing: 'border-box',
  background: '#0b0b0b',
  color: '#f9fafb',
  border: '1px solid #374151',
  borderRadius: '8px',
  padding: '10px 12px',
};

export default AnnouncementManager;
